import { useEffect, useReducer } from 'react';
import CheckboxFieldComponent from '@/components/common/notes/show-note-forms/form-components/checkbox-field-component';
import DateFieldComponent from '@/components/common/notes/show-note-forms/form-components/date-field-component';
import HeaderFieldComponent from '@/components/common/notes/show-note-forms/form-components/header-field-component';
import HiddenFieldComponent from '@/components/common/notes/show-note-forms/form-components/hidden-field-component';
import NumberFieldComponent from '@/components/common/notes/show-note-forms/form-components/number-field-component';
import RadioFieldComponent from '@/components/common/notes/show-note-forms/form-components/radio-field-component';
import SelectFieldComponent from '@/components/common/notes/show-note-forms/form-components/select-field-component';
import TextFieldComponent from '@/components/common/notes/show-note-forms/form-components/text-field-component';
import { formReducer, formReducerInitialState } from '@/components/common/notes/show-note-forms/formReducer';
import { SET_VALUE } from '@/components/common/notes/show-note-forms/dataToSendReducer';
import decodeHtmlEntities from '@/utils/decodeHtmlEntities';

interface Props {
    nodeForm: any;
    dataToSendState: any;
    dataToSendStateDispatch: any;
    required: boolean;
    index: string;
    readOnly: boolean;
    formId?: string;
}

const GlobalNoteDialogFormField = ({
    nodeForm,
    dataToSendStateDispatch,
    dataToSendState,
    required,
    index,
    readOnly = false,
    formId: formIdProp,
}: Props) => {
    const [formState, formDispatch]: any = useReducer<any, any>(
        formReducer,
        'forms' in dataToSendState && index in dataToSendState.forms
            ? dataToSendState.forms[index].toSend
            : formReducerInitialState,
    );

    const formData = JSON.parse(nodeForm.data.formData);

    const formId = formIdProp ? formIdProp : nodeForm.data.id;
    const formName = nodeForm.data.name;

    const validateObject = (data: any) => {
        const keys = Object.keys(data);
        let out = false;

        keys.forEach((key) => {
            if (data[key]) out = true;
        });

        return !out;
    };

    const validateFields = () => {
        let requiredFields: any[] = [];

        formData.forEach((item: any) => {
            if (item?.required) requiredFields.push(item.name);
        });

        let out = requiredFields.length === 0;

        if (!out) {
            out = true;
            requiredFields.forEach((key) => {
                if (
                    formId in formState &&
                    key in formState[formId] &&
                    (formState[formId][key] !== null && typeof formState[formId][key] === 'object'
                        ? validateObject(formState[formId][key])
                        : formState[formId][key] === '')
                ) {
                    out = false;
                }
            });
        }

        return out;
    };

    useEffect(() => {
        dataToSendStateDispatch({
            type: SET_VALUE,
            payload: {
                key: 'forms',
                data: {
                    toSend: formState,
                    required: required,
                    valid: validateFields(),
                },
                index,
            },
        });
    }, [formState]);

    const isFirstInputText = (item: any) => {
        let firstInputTextName: string | null = null;
        formData.forEach((value: any) => {
            const { type } = value;
            if ((type === 'text' || type === 'textarea' || type === 'date') && !firstInputTextName) {
                firstInputTextName = value.name;
            }
        });
        return !!firstInputTextName && item.name === firstInputTextName;
    };

    const renderComponent = (item: any, index: number) => {
        const { type, name, label } = item;

        const key: string = `${name}${index}`;
        const commonProps = {
            formDispatch,
            formId,
            formName,
            value: formId in formState && name in formState[formId] ? formState[formId][name] : '',
            item: { ...item, label: decodeHtmlEntities(item.label) },
            dataTestSc: name,
            autoFocus: isFirstInputText(item),
            readOnly,
        };

        if (label === 'hidden') {
            return <HiddenFieldComponent key={key} {...commonProps} />;
        }

        switch (type) {
            case 'header': {
                return <HeaderFieldComponent key={key} item={item} />;
            }
            case 'text':
            case 'textarea': {
                return <TextFieldComponent key={key} {...commonProps} />;
            }
            case 'date': {
                return <DateFieldComponent key={key} {...commonProps} />;
            }
            case 'number': {
                return <NumberFieldComponent key={key} {...commonProps} />;
            }
            case 'hidden': {
                return <HiddenFieldComponent key={key} {...commonProps} />;
            }
            case 'select': {
                return (
                    <SelectFieldComponent
                        key={key}
                        {...commonProps}
                        value={formId in formState && name in formState[formId] ? formState[formId][name] : null}
                    />
                );
            }
            case 'radio-group': {
                return (
                    <RadioFieldComponent
                        key={key}
                        {...commonProps}
                        value={formId in formState && name in formState[formId] ? formState[formId][name] : null}
                    />
                );
            }
            case 'checkbox-group': {
                return (
                    <CheckboxFieldComponent
                        key={key}
                        {...commonProps}
                        value={formId in formState && name in formState[formId] ? formState[formId][name] : null}
                    />
                );
            }
            default: {
                return null;
            }
        }
    };

    return formData.map((item: any, index: number) => renderComponent(item, index));
};

export default GlobalNoteDialogFormField;
